import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer } from 'recharts';

/**
 * DistributionChart - Bell curve visualization with vertical reference line
 * 
 * @param {Object} props
 * @param {number} props.mean - Mean value for the distribution
 * @param {number} props.stdDev - Standard deviation
 * @param {number} props.currentValue - Current value to show as vertical line
 * @param {string} props.title - Chart title
 * @param {string} props.xAxisLabel - X-axis label
 * @param {number} props.minValue - Minimum value for X-axis (optional)
 * @param {number} props.maxValue - Maximum value for X-axis (optional)
 * @param {number} props.points - Number of points to generate (default: 100)
 */
export default function DistributionChart({
  mean,
  stdDev,
  currentValue,
  title,
  xAxisLabel,
  minValue,
  maxValue,
  points = 100
}) {
  // Validate inputs
  if (mean === undefined || mean === null || stdDev === undefined || stdDev === null || stdDev <= 0) {
    return (
      <div className="chart-empty">
        No distribution data available
      </div>
    );
  }

  // Calculate the actual domain for the chart (returns are in centered space, can be negative)
  const rangeMin = minValue !== undefined ? minValue : mean - 4 * stdDev;
  const rangeMax = maxValue !== undefined ? maxValue : mean + 4 * stdDev;

  // Generate normal distribution curve data using the exact formula provided
  const normalPDF = (x, mean, std) => {
    const z = (x - mean) / std;
    return (1 / (std * Math.sqrt(2 * Math.PI))) * Math.exp(-0.5 * z * z);
  };
  
  const generateDistributionData = () => {
    const data = [];
    const step = (rangeMax - rangeMin) / points;
    
    for (let i = 0; i <= points; i++) {
      const x = rangeMin + i * step;
      // Use the exact normalPDF function - returns probability density
      const density = normalPDF(x, mean, stdDev);
      
      // Store the raw density value (this is the actual probability density, not a percentage)
      data.push({
        x: parseFloat(x.toFixed(3)),
        y: parseFloat(density.toFixed(6))
      });
    }
    
    return data;
  };

  const data = generateDistributionData();
  
  // Check if we should show the reference line - be more lenient with validation
  const shouldShowLine = currentValue !== undefined && 
                          currentValue !== null && 
                          !isNaN(currentValue) &&
                          isFinite(currentValue);
  
  // Clamp the value to the domain if it's outside (so the line still shows)
  const clampedValue = shouldShowLine ? Math.max(rangeMin, Math.min(rangeMax, currentValue)) : null;

  // Error function approximation (Abramowitz and Stegun)
  const erf = (x) => {
    const sign = x >= 0 ? 1 : -1;
    x = Math.abs(x);

    const a1 = 0.254829592;
    const a2 = -0.284496736;
    const a3 = 1.421413741;
    const a4 = -1.453152027;
    const a5 = 1.061405429;
    const p = 0.3275911;

    const t = 1 / (1 + p * x);
    const y = 1 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);

    return sign * y;
  };

  // Cumulative distribution function for normal distribution
  const normalCDF = (x, mean, std) => {
    return 0.5 * (1 + erf((x - mean) / (std * Math.sqrt(2))));
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const xValue = payload[0].payload.x;
      // Calculate probability that value is greater than xValue
      // P(X > x) = 1 - P(X <= x) = 1 - normalCDF(x, mean, std)
      const probGreater = 1 - normalCDF(xValue, mean, stdDev);
      const probGreaterPercent = (probGreater * 100).toFixed(2);
      
      return (
        <div style={{
          backgroundColor: '#15121D',
          padding: '8px 12px',
          borderRadius: '8px',
          border: '1px solid #3A3349'
        }}>
          <p style={{ margin: 0, color: '#ECE9F3', fontSize: '12px' }}>
            {xAxisLabel}: {xAxisLabel === 'Return' ? `${xValue >= 0 ? '+' : ''}${xValue.toFixed(2)}%` : xValue.toFixed(3)}
          </p>
          <p style={{ margin: '4px 0 0 0', color: '#9A93AB', fontSize: '11px' }}>
            Density: {payload[0].payload.y.toFixed(4)}
          </p>
          <p style={{ margin: '4px 0 0 0', color: '#9A93AB', fontSize: '11px' }}>
            P(X &gt; {xAxisLabel === 'Return' ? `${xValue >= 0 ? '+' : ''}${xValue.toFixed(2)}%` : xValue.toFixed(3)}): {probGreaterPercent}%
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="chart-block">
      <h3>
        {title}
      </h3>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={data} margin={{ top: 40, right: 30, left: 0, bottom: 40 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#2A2536" />
          <XAxis 
            type="number"
            dataKey="x" 
            domain={[rangeMin, rangeMax]}
            tickFormatter={(value) => {
              if (isNaN(value) || !isFinite(value)) return '';
              // Returns (centered at 0): show as +24, -5
              if (xAxisLabel === 'Return') {
                const sign = value >= 0 ? '+' : '';
                return `${sign}${value.toFixed(1)}`;
              }
              return Math.abs(value) < 0.01 ? value.toFixed(4) : value.toFixed(3);
            }}
            stroke="#6E6780"
            tick={{ fill: '#9A93AB', fontSize: 12 }}
            angle={-45}
            textAnchor="end"
            height={60}
            label={{ 
              value: xAxisLabel, 
              position: 'insideBottom', 
              offset: 5,
              style: { fill: '#9A93AB', fontSize: 12 }
            }}
          />
          <YAxis 
            stroke="#6E6780"
            tick={{ fill: '#9A93AB', fontSize: 12 }}
            label={{ 
              value: 'Probability Density', 
              angle: -90, 
              position: 'insideLeft',
              style: { fill: '#9A93AB', fontSize: 12 }
            }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Line 
            type="monotone" 
            dataKey="y" 
            stroke="#9B72E3" 
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4, fill: '#9B72E3' }}
          />
          {xAxisLabel === 'Return' && (
            <ReferenceLine 
              x={0} 
              stroke="#6E6780" 
              strokeWidth={2}
              strokeDasharray="4 4"
            />
          )}
          {shouldShowLine && clampedValue !== null && (
            <ReferenceLine 
              x={clampedValue} 
              stroke="#FF6B6B" 
              strokeWidth={4}
              strokeOpacity={1}
              label={{ 
                value: 'Last event', 
                position: 'top',
                fill: '#FF6B6B',
                fontSize: 14,
                fontWeight: 700,
                offset: 15
              }}
            />
          )}
        </LineChart>
      </ResponsiveContainer>
      <div style={{ 
        marginTop: '0.5rem', 
        fontSize: '0.85rem', 
        color: '#9A93AB',
        textAlign: 'center'
      }}>
        Mean: {xAxisLabel === 'Return' ? `${mean >= 0 ? '+' : ''}${mean.toFixed(2)}%` : mean.toFixed(3)} | Std Dev: {xAxisLabel === 'Return' ? `${stdDev.toFixed(2)}%` : stdDev.toFixed(3)}
        {currentValue !== undefined && currentValue !== null && (
          <span> | Current: {xAxisLabel === 'Return' ? `${currentValue >= 0 ? '+' : ''}${currentValue.toFixed(2)}%` : currentValue.toFixed(3)}</span>
        )}
      </div>
    </div>
  );
}

